import React, { useEffect, useState } from "react";
import { Form, Input, Row, Col, Select } from "antd";
import { toUiPosition } from "../../utils/generalUtils";

import TextField from "./fields/TextField";
import TextAreaField from "./fields/TextAreaField";
import DateField from "./fields/DateField";
import DateRangeField from "./fields/DateRangeField";
import SelectField from "./fields/SelectField";
import MultipleSelectField from "./fields/MultipleSelectField";
import PositionDropdownField from "./fields/PositionDropdownField";
import RadioTextBoxField from "./fields/RadioTextBoxField";
import PeriodSelectorField from "./fields/PeriodSelectorField";
import AbsenceDateSelectorField from "./fields/AbsenceDateSelectorField";
import AccomplishmentsListField from "./fields/AccomplishmentsListField";
import TermSelect from "../TermSelect";

const DynamicFormFields = ({
  fields,
  form,
  radioStates = {},
  onRadioChange,
  previousData,
}) => {
  useEffect(() => {
    fields.forEach((field) => {
      if (field.type !== "position-dropdown") return;

      const currentId = form.getFieldValue(field.name);
      if (!currentId) return;

      const match = field.dropdownItems?.find(
        (p) => p.position_id === currentId,
      );
      if (match) {
        const normalized = toUiPosition(match);
        form.setFieldsValue({
          [field.inputName]:
            normalized?.complete_position_name || normalized?.position_name,
        });
      }
    });
  }, [fields, form]);

  const lastSubmission = previousData?.[previousData.length - 1];
  const previousAccomplishments = lastSubmission?.accomplishments;

  const [chairOptions, setChairOptions] = useState([]);
  const [deanOptions, setDeanOptions] = useState([]);
  const selectedPositionId = Form.useWatch("position_id", form);

  // Fetch program chairs and deans for dropdown options
  useEffect(() => {
    const fetchChairsAndDeans = async () => {
      try {
        const token = localStorage.getItem("api_token");

        const [chairsRes, deansRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/position/chairs`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/position/deans`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const chairsData = (await chairsRes.json()).sort((a, b) =>
          a.unit.localeCompare(b.unit),
        );
        const deansData = (await deansRes.json()).sort((a, b) =>
          a.unit.localeCompare(b.unit),
        );

        setChairOptions(
          chairsData.map((p) => ({
            label: `${p.position_name} - ${p.unit}`,
            value: p.id,
          })),
        );
        setDeanOptions(
          deansData.map((p) => ({
            label: `${p.position_name} - ${p.unit}`,
            value: p.id,
            unit: p.unit,
          })),
        );
      } catch (err) {
        console.error("Failed to fetch chairs and deans", err);
      }
    };

    fetchChairsAndDeans();
  }, []);

  // Auto-select dean based on selected position
  useEffect(() => {
    if (!selectedPositionId || !deanOptions.length || !fields.length) {
      form.setFieldsValue({
        dean: undefined,
        dean_label: "",
      });
      return;
    }

    const selectedPosition = fields
      .find((f) => f.name === "position_id")
      ?.dropdownItems?.find((p) => p.position_id === selectedPositionId);

    if (!selectedPosition) {
      form.setFieldsValue({
        dean: undefined,
        dean_label: "",
      });
      return;
    }

    const matchedDean = deanOptions.find(
      (dean) => dean.unit === selectedPosition.unit,
    );

    if (matchedDean) {
      form.setFieldsValue({
        dean: matchedDean.value,
        dean_label: matchedDean.label,
      });
    } else {
      form.setFieldsValue({
        dean: undefined,
        dean_label: "",
      });
    }
  }, [selectedPositionId, deanOptions, fields, form]);

  const renderField = (field) => {
    if (field.type === "radiotextbox") {
      return (
        <RadioTextBoxField
          key={field.name}
          field={field}
          radioStates={radioStates}
          onRadioChange={onRadioChange}
        />
      );
    }

    if (field.type === "daterange") {
      return <DateRangeField key={field.name} field={field} form={form} />;
    }

    if (field.type === "position-dropdown") {
      return (
        <PositionDropdownField key={field.name} field={field} form={form} />
      );
    }

    if (field.type === "select") {
      return <SelectField key={field.name} field={field} />;
    }

    if (field.type === "multiple-select") {
      return <MultipleSelectField key={field.name} field={field} />;
    }

    if (field.type === "period-selector") {
      return (
        <React.Fragment key={field.name}>
          <Form.Item
            hidden={field?.hidden}
            name={field.name}
            label={field.label}
            extra={field.extra}
            rules={field.rules}
          >
            <PeriodSelectorField
              onPeriodChange={(period) => {
                form.setFieldsValue({
                  start_date: period?.startDate ?? null,
                  end_date: period?.endDate ?? null,
                  absent_dates: "",
                });
              }}
            />
          </Form.Item>
          <Form.Item name="start_date" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="end_date" hidden>
            <Input />
          </Form.Item>
        </React.Fragment>
      );
    }

    if (field.type === "absence-chip-selector") {
      return (
        <Form.Item
          key={field.name}
          hidden={field?.hidden}
          name={field.name}
          label={field.label}
          extra={field.extra}
          rules={field.rules}
        >
          <AbsenceDateSelectorField form={form} />
        </Form.Item>
      );
    }

    if (field.type === "accomplishments-list") {
      return (
        <Form.Item
          key={field.name}
          hidden={field?.hidden}
          name={field.name}
          label={field.label}
          extra={field.extra}
          rules={field.rules}
        >
          <AccomplishmentsListField
            previousAccomplishments={previousAccomplishments}
          />
        </Form.Item>
      );
    }

    if (field.type === "textarea") {
      return <TextAreaField key={field.name} field={field} />;
    }

    if (field.type === "date") {
      return <DateField key={field.name} field={field} />;
    }

    if (field.type === "term-select") {
      return (
        <Form.Item
          key={field.name}
          hidden={field?.hidden}
          name={field.name}
          label={field.label}
          extra={field.extra}
          rules={field.rules}
        >
          <TermSelect />
        </Form.Item>
      );
    }

    if (field.type === "appointment-status-select") {
      return (
        <Form.Item
          key={field.name}
          hidden={field?.hidden}
          name={field.name}
          label={field.label}
          extra={field.extra}
          rules={field.rules}
        >
          <Select
            showSearch
            style={{ width: "100%" }}
            options={[
              { label: "Permanent", value: "Permanent" },
              { label: "Temporary", value: "Temporary" },
              { label: "Substitute", value: "Substitute" },
            ]}
          />
        </Form.Item>
      );
    }

    if (field.type === "chair-select") {
      return (
        <Form.Item
          key={field.name}
          hidden={field?.hidden}
          name={field.name}
          label={field.label}
          extra={field.extra}
          rules={field.rules}
        >
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            options={chairOptions}
            optionFilterProp="label"
          />
        </Form.Item>
      );
    }

    if (field.type === "dean-select") {
      return (
        <React.Fragment key={field.name}>
          <Form.Item name="dean" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="dean_label" label={field.label} rules={field.rules}>
            <Input disabled placeholder="No dean found for selected position" />
          </Form.Item>
        </React.Fragment>
      );
    }

    return <TextField key={field.name} field={field} />;
  };

  const hasTwoColumn = fields.some(
    (f) => f.column === "left" || f.column === "right",
  );

  if (hasTwoColumn) {
    const leftFields = fields.filter((f) => f.column !== "right");
    const rightFields = fields.filter((f) => f.column === "right");
    return (
      <Row gutter={24} align="top">
        <Col
          span={12}
          style={{ borderRight: "1px solid #f0f0f0", paddingRight: 16 }}
        >
          {leftFields.map((field) => renderField(field))}
        </Col>
        <Col span={12}>{rightFields.map((field) => renderField(field))}</Col>
      </Row>
    );
  }

  return fields.map((field) => renderField(field));
};

export default DynamicFormFields;
